import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Button, DateRangeMobi, Stack } from '@esgian/esgianui';
import moment from 'moment';
import { LocationSelect } from '@components/Inputs';
import { useDispatch, useSelector } from 'react-redux';
import { getDateFormat } from '@store/features';
import { getMonthDateRangePickerSx, getStartEndMonth } from '@helpers/DatePickerHelpers';
import { useLookupOperatorsQuery, useSegment } from '@hooks';
import OperatorAdvancedMultiSelect from '@components/Inputs/OperatorAdvancedSelect';
import {
  getPortActivityFilters,
  updatePortActivityFilters
} from '@store/features/filters/CommercialAnalyticsPage/PortActivitySlice/PortActivitySlice';
import { useLookupActiveLocations } from '@hooks/useDynamicLookupQueries/useLookupActiveLocations';
import { useLookupCountries } from '@hooks/useStaticLookupQueries/useLookupCountryQuery';
import { GEOGRAPHICAL_TYPES } from '@constants';
function locationSort(a, b) {
  return a?.name?.localeCompare(b?.name);
}
const importOrder = ['Region', 'Country', 'Port'];

const sortByLocation = importOrder.reduce((obj, item, index) => {
  return {
    ...obj,
    [item]: index
  };
}, {});
function PortActivityFilters() {
  const dispatch = useDispatch();
  const portActivityFilters = useSelector(getPortActivityFilters);
  const dateFormat = useSelector(getDateFormat);
  const { segmentMinDate } = useSegment();
  const { data: lookupOperators } = useLookupOperatorsQuery();
  const { data: lookupActiveLocations } = useLookupActiveLocations();
  const { data: lookupCountry } = useLookupCountries();
  const [tempFilters, setTempFilters] = useState(portActivityFilters);

  useEffect(() => {
    setTempFilters({ ...portActivityFilters });
  }, [portActivityFilters]);
  const updateTempFilterValue = (payload) => {
    setTempFilters((prevFilters) => ({ ...prevFilters, ...payload }));
  };

  const handleApply = () => {
    dispatch(updatePortActivityFilters(tempFilters));
  };

  const { startDate, endDate, selectedLocation, selectedOperators, selectType } = tempFilters;

  const checkValidFilters = selectedLocation && startDate && endDate;

  const lookupLocations = useMemo(() => {
    if (!lookupActiveLocations.length || !lookupCountry) return [];
    return lookupActiveLocations
      .map((item) => {
        const val = {
          name: item.displayName,
          id: item.geoId,
          isoAlpha2: null,
          locode: null
        };
        if (item.geoType === GEOGRAPHICAL_TYPES.PORT) {
          const match = item.displayName.match(/^(.*?)\s*\((.*?)\)$/);
          val['type'] = 'Port';
          val['name'] = match[1];
          val['locode'] = match[2];
        }
        if (item.geoType === GEOGRAPHICAL_TYPES.REGION) {
          val['type'] = 'Region';
        }
        if (item.geoType === GEOGRAPHICAL_TYPES.COUNTRY) {
          val['type'] = 'Country';
          val['isoAlpha2'] = lookupCountry[item.geoId].isoAlpha2;
        }
        return val;
      })
      .sort(locationSort)
      ?.sort((a, b) => sortByLocation[a.type] - sortByLocation[b.type]);
  }, [lookupActiveLocations, lookupCountry]);

  return (
    <Grid item xs={12}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Stack className={'section-filter'}>
            <LocationSelect
              label={'Location*'}
              placeholder={'Search by port, city or region'}
              handleChange={(val) => {
                updateTempFilterValue({ selectedLocation: val });
              }}
              selectedLocation={selectedLocation}
              locationList={lookupLocations}
            />
          </Stack>
          <Stack className={'section-filter'}>
            <DateRangeMobi
              sx={getMonthDateRangePickerSx(startDate, endDate, 'Period*')}
              onClose={({ value }) => {
                const { start, end } = getStartEndMonth(value, dateFormat);
                updateTempFilterValue({
                  startDate: start,
                  endDate: end
                });
              }}
              dateFormat={dateFormat}
              minStartDate={segmentMinDate}
              disableFuture
              startDate={moment(startDate, dateFormat)}
              endDate={moment(endDate, dateFormat)}
              placeholder={'Period'}
              dateWheels={'MMM YYYY'}
            />
          </Stack>
          <Stack className={'section-filter'}>
            <OperatorAdvancedMultiSelect
              selected={selectedOperators}
              autoEnabledText={'Displaying the top 5 operators given the selected period'}
              handleClose={(values) => {
                updateTempFilterValue({
                  selectType: values.operatorType,
                  selectedOperators: values.selectedOperators
                });
              }}
              loading={!lookupOperators}
              autoButtonTitle={'Top 5'}
              operators={lookupOperators}
              selectType={selectType}
              label={'Operators*'}
              showAdvanceButton={true}
            />
          </Stack>

          <Button
            onClick={handleApply}
            variant={'contained'}
            color={'primary'}
            sx={{ height: 'min-content' }}
            disabled={!checkValidFilters}>
            Apply Filters
          </Button>
        </Stack>
      </Stack>
    </Grid>
  );
}

PortActivityFilters.propTypes = {};

PortActivityFilters.defaultProps = {};

export default PortActivityFilters;
